import type { AiQuestionAnswer } from "./types";
import { normalizeKbQuestionKey } from "./normalizeKbQuestionKey";

export type ChunkQuality = {
  chunkCount: number;
  totalChunkChars: number;
  avgChunkChars: number;
  minChunkChars: number;
  maxChunkChars: number;
  risk: "low" | "moderate" | "healthy";
};

export function adviseFromChunkQuality(input: ChunkQuality | null): string[] {
  if (!input) return [];
  if (input.risk === "healthy") {
    return ["Chunk coverage looks healthy for retrieval."];
  }
  if (input.risk === "moderate") {
    return [
      "Add one or two concrete policy/process examples to strengthen retrieval context.",
      "Use preview mode to verify segments map to your intended question patterns.",
    ];
  }
  return [
    "Expand the article body before publish so retrieval has enough canonical detail.",
    "Consider splitting broad topics into separate focused articles, then resave.",
  ];
}

export function mergeAiQuestions(previous: AiQuestionAnswer[], nextQuestions: string[]): AiQuestionAnswer[] {
  const previousByKey = new Map(previous.map((item) => [normalizeKbQuestionKey(item.question), item]));
  const nextKeys = new Set<string>();
  const merged: AiQuestionAnswer[] = nextQuestions.map((question) => {
    const key = normalizeKbQuestionKey(question);
    nextKeys.add(key);
    const existing = previousByKey.get(key);
    return existing ? { ...existing, question } : { question, answer: "", disposition: "open" as const };
  });

  const answeredHistory = previous.filter((item) => item.disposition !== "open" && !nextKeys.has(normalizeKbQuestionKey(item.question)));
  return [...merged, ...answeredHistory];
}

export function updateAiQuestionAnswer(
  previous: AiQuestionAnswer[],
  question: string,
  answer: string
): AiQuestionAnswer[] {
  const key = normalizeKbQuestionKey(question);
  let found = false;
  const next: AiQuestionAnswer[] = previous.map((item) => {
    if (normalizeKbQuestionKey(item.question) !== key) return item;
    found = true;
    return { ...item, question, answer, disposition: answer.trim() ? "answered" : item.disposition === "answered" ? "open" : item.disposition };
  });
  return found ? next : [...next, { question, answer, disposition: answer.trim() ? ("answered" as const) : ("open" as const) }];
}

export function setAiQuestionDisposition(
  previous: AiQuestionAnswer[],
  question: string,
  disposition: AiQuestionAnswer["disposition"]
): AiQuestionAnswer[] {
  const key = normalizeKbQuestionKey(question);
  return previous.map((item) => (normalizeKbQuestionKey(item.question) !== key ? item : { ...item, disposition }));
}
